import React, { createContext, useState, useContext } from 'react';

const translations = {
    EN: {
        // Sidebar & Layout
        dashboard: 'Dashboard',
        voiceAgents: 'Voice Agents',
        users: 'Users',
        analytics: 'Performance',
        training: 'Training',
        batchCalling: 'Batch Calling',
        callLogs: 'Call Logs',
        billing: 'Billing',
        settings: 'Settings',
        searchPlaceholder: 'Search performance, agents or logs...',
        notifications: 'Notifications',
        logout: 'Log Out',
        profileSettings: 'Profile Settings',
        account: 'Account',
        signOut: 'Sign Out',
        administrator: 'Administrator',

        // Page Titles
        analyticsDashboard: 'Dashboard',
        performanceAnalytics: 'Performance',
        agentTraining: 'Agent Training',
        batchHistory: 'Batch History',
        createBatchCall: 'Create Batch Call',
        subscriptionBilling: 'Subscription & Billing',
        accountSettings: 'Account Settings',
        userProfile: 'User Profile',
        userManagement: 'User Management',
        createVoiceAgent: 'Create Voice Agent',
        editVoiceAgent: 'Edit Voice Agent',
        retrainAgent: 'Retrain Agent',
        platformOverview: 'Platform Overview',
        aiAgents: 'AI Agents',

        // Dashboard Common
        growthVsLastMonth: 'Growth vs Last Month',
        totalCalls: 'Total Calls',
        successRate: 'Success Rate',
        avgCallDuration: 'Avg Call Duration',
        activeAgents: 'Active Agents',
        dailyCallVolume: 'Daily Call Volume',
        callTracking30d: 'Call tracking for the last 30 days',
        monthly: 'Monthly',
        weekly: 'Weekly',
        successRateDistribution: 'Success Rate Distribution',
        callOutcomesBreakdown: 'Call outcomes breakdown',
        success: 'Success',
        successful: 'Successful',
        failed: 'Failed',
        noAnswer: 'No Answer',
        weeklyPerformance: 'Weekly Performance',
        callsPerDay: 'Calls per day',
        calls: 'Calls',
        agentPerformance: 'Agent Performance',
        individualStats: 'Individual stats',
        active: 'Active',
        busy: 'Busy',
        fullReport: 'Full report',
        newThisWeek: 'New This Week',
        inactiveAgents: 'Inactive Agents',
        activeAgent: 'Active Agent',
        docsCoverage: 'Docs Coverage',
        yourAiAgents: 'Your AI Agents',
        recentActivity: 'Recent Activity',
        agentCreated: 'Agent created',
        callsSoFar: 'calls so far',
        successRatePercent: 'Success rate',
        createBatchCall: 'Create Batch Call',
        viewCallLogs: 'View Call Logs',

        // SuperAdmin Dashboard
        welcomeBack: 'Welcome back',
        happeningPlatform: "Here's what's happening across the platform.",
        platformSettings: 'Platform Settings',
        totalUsers: 'Total Users',
        activeSubscriptions: 'Active Subscriptions',
        newUsers7d: 'New Users (7d)',
        recentUsers: 'Recent Users',
        viewAll: 'View All',
        noUsersYet: 'No users yet.',
        topPerformingAgentTypes: 'Top Performing Agent Types',
        totalCallsCounter: 'total calls',
        noAgentData: 'No agent performance data yet.',
        topRatedAgents: 'Top Rated Agents',
        noRatedAgents: 'No agents with enough calls yet.',
        quickActions: 'Quick Actions',
        createNewAgent: 'Create New Agent',
        viewAnalytics: 'View Analytics',

        // Agent List & Types
        manageAgents: 'Manage and monitor your AI agents',
        viewingAgentsFor: 'Viewing agents for',
        searchAgents: 'Search agents...',
        allTypes: 'All Types',
        allStatus: 'All Status',
        customer_support: 'Standard',
        lead_generation: 'Emergency',
        appointment_scheduling: 'After-hours / Info',
        unknown: 'Unknown',
        support: 'Standard',
        leads: 'Emergency',
        appts: 'After-hours / Info',
        other: 'Other',
        inactive: 'Inactive',
        avgDur: 'Avg Dur',
        created: 'Created',
        editAgent: 'Edit Agent',
        deleteAgent: 'Delete Agent',
        pause: 'Pause',
        resume: 'Resume',
        pausing: 'Pausing...',
        resuming: 'Resuming...',
        noAgentsFound: 'No agents found',
        noAgentsFor: 'No agents for',
        notCreatedAgents: "You haven't created any AI agents yet.",
        createAgentToStart: 'create an agent',
        toGetStarted: 'to get started.',
        confirmDelete: 'Are you sure you want to delete',
        cannotBeUndone: 'This action cannot be undone.',
        cancel: 'Cancel',
        deleting: 'Deleting...',
        editingDisabled: 'Editing disabled for this agent',
        failedToLoadAgentStats: 'Failed to load agent stats',
        errorFetchingAgents: 'Error fetching agents',
        errorDeletingAgent: 'Error deleting agent',
        agentDeletedSuccess: 'Agent deleted successfully',
        agentStatusUpdated: 'Agent status updated successfully',
        errorUpdatingStatus: 'Error updating agent status',

        // Call Logs
        viewCallsAndTranscripts: 'View call recordings and transcripts',
        exportCsv: 'Export CSV',
        totalDuration: 'Total Duration',
        searchCalls: 'Search calls...',
        allAgents: 'All Agents',
        completed: 'Completed',
        running: 'Running',
        inProgress: 'In Progress',
        missed: 'Missed',
        noCallHistory: 'No call history yet',
        noCallsYetDesc: "You don't have any calls yet. Try adjusting filters, or start making calls to see them here.",
        clearSearch: 'Clear Search',
        resetFilters: 'Reset Filters',
        callInfo: 'Call Info',
        customer: 'Customer',
        agent: 'Agent',
        duration: 'Duration',
        status: 'Status',
        summaryLabel: 'Summary',
        inbound: 'Inbound',
        outbound: 'Outbound',
        view: 'View',
        showing: 'Showing',
        to: 'to',
        of: 'of',
        results: 'results',
        previous: 'Previous',
        next: 'Next',
        callSummary: 'Call Summary',
        detailedBreakdown: 'Detailed conversation breakdown',
        close: 'Close',
        noSummaryAvailable: 'No summary available for this call.',
        unableToLoadSummary: 'Unable to load summary at this time. Please try again later.',

        // User List
        allUsers: 'All Users',
        manageUsers: 'Manage and monitor your users',
        withAgents: 'With Agents',
        withoutAgents: 'Without Agents',
        searchUsers: 'Search users...',
        searchUsersPlaceholder: 'Search by name, email, company, role, or plan...',
        allVerification: 'All Verification',
        verified: 'Verified',
        unverified: 'Unverified',
        allActivity: 'All Activity',
        company: 'Company',
        minutes: 'Minutes',
        joined: 'Joined',
        noUsersYetTitle: 'No users yet',
        noUsersYetDesc: "You haven't added any users yet. Once users join, they'll appear here for quick access.",
        viewUsersAgents: "View user's agents",

        // Billing
        billingAndSub: 'Billing & Subscriptions',
        manageSub: 'Manage your subscription and billing information',
        plansPricing: 'Plans & Pricing',
        currentSub: 'Current Subscription',
        billingHistory: 'Billing History',
        choosePlan: 'Choose Your Plan',
        selectPlanDesc: 'Select the plan that best fits your needs',
        comparePlans: 'Compare Plans',
        mostPopular: 'Most Popular',
        activeSubscription: 'Active Subscription',
        currentPlan: 'Current Plan',
        contactSales: 'Contact Sales',
        freeTier: 'Free Tier',
        choose: 'Choose',
        redirecting: 'Redirecting...',
        cancelSubscription: 'Cancel Subscription',
        cancelSubDesc: 'This will end your paid plan at period end and move you to the ',
        keep: 'Keep',
        confirm: 'Confirm',
        comparePlansTitle: 'Compare Plans',
        feature: 'Feature',
        monthlyPrice: 'Monthly Price',
        setupFee: 'Setup Fee',
        includedMinutes: 'Included Minutes',
        extraMinuteRate: 'Extra Minute Rate',
        included: 'Included',
        no: 'No',
        paymentHistory: 'Payment History',
        export: 'Export',
        redirectingToCheckout: 'Redirecting to checkout...',
        subscriptionUpdated: 'Subscription updated.',
        checkoutCancelled: 'Checkout cancelled.',
        noPaymentLink: 'No downloadable link is available for this payment yet.',
        month: 'month',
        perMonth: 'per month',
        customPricing: 'Custom Pricing',
        setup: 'Setup',
        monthly: 'Monthly',
        extra: 'Extra',
        monthlyCost: 'Monthly Cost',
        autoRenewal: 'Auto-Renewal',
        enabled: 'Enabled',
        disabled: 'Disabled',
        usageThisMonth: 'Usage This Month',
        minutesUsed: 'Minutes Used',
        remaining: 'Remaining',
        overage: 'Overage',
        changePlan: 'Change Plan',
        billingHistoryTitle: 'Billing History',
        payments: 'payments',
        searchPayments: 'Search payments...',
        noPaymentsFound: 'No payments found',
        billingHistoryEmptyDesc: 'Your billing history will appear here once you have payments.',
        created: 'Created',
        invoiceId: 'Invoice ID',
        description: 'Description',
        amount: 'Amount',
        status: 'Status',
        actions: 'Actions',
        download: 'Download',
        loading: 'Loading...',
        loadMore: 'Load more',
        noMorePayments: 'No more payments',
        shown: 'shown',
        ofSuffix: 'of',

        // Analytics / Performance
        analytics: 'Performance',
        analyticsDesc: 'Detailed insights into your AI agent performance',
        totalCalls: 'Total Calls',
        successRate: 'Success Rate',
        avgCallDuration: 'Avg Call Duration',
        activeAgents: 'Active Agents',

        // Performance Blocks
        callsReceived: 'Calls Received',
        totalCallsMonth: 'Total Calls This Month',
        callsHandledAI: 'Calls Fully Handled by AI',
        managedByValoria: 'Calls Successfully Managed by Valoria',
        callsTransferredHuman: 'Calls Transferred to Human',
        escalatedToTeam: 'Calls Escalated to Your Team',
        appointmentsConfirmedAI: 'Appointments Confirmed by AI',
        bookedByValoria: 'Appointments Booked by Valoria',
        timeSaved: 'Time Saved',
        assistantTimeSaved: 'Assistant Time Saved',
        emergenciesDetected: 'Emergencies Detected',
        urgentCasesIdentified: 'Urgent Cases Identified & Secured',
        afterHoursCalls: 'After-Hours Calls',
        capturedOutsideHours: 'Calls Captured Outside Opening Hours',
        minutesUsed: 'Minutes Used',
        planUsageStatus: 'Plan Usage Status',

        callsByHour: 'Calls by Hour',
        callsByDay: 'Calls by Day',
        distCallsHour: 'Distribution of calls across hours',
        dailyCallVolume: 'Daily call volume',
        weeklyPerformance: 'Weekly Performance',
        callsPerDaySuccess: 'Calls per day with success rate',
        agentPerformance: 'Agent Performance',
        individualAgentStats: 'Individual agent statistics',
        noWeeklyData: 'No weekly data for this selection.',
        noAgentData: 'No agent performance data available.',
        noBreakdownData: 'No hourly or daily breakdown available for this selection.',
        successful: 'Successful',
        calls: 'calls',
        success: 'success',
        avg: 'avg',
        exportReport: 'Export Report',

        // Settings
        settings: 'Settings',
        settingsDesc: 'Configure your account settings',
        accountSettings: 'Account Settings',
        accountSettingsDesc: 'Manage your profile and account preferences',
        profileInfo: 'Profile Information',
        goToProfile: 'Go to Profile',
        passwordSecurity: 'Password & Security',
        manage: 'Manage',
        billingSub: 'Billing & Subscription',
        billingSubDesc: 'Manage your subscription and billing',
        currentPlanLabel: 'Current Plan',
        viewDetails: 'View Details',
        advanced: 'Advanced',
        advancedDesc: 'Advanced settings and configurations',
        advancedSettings: 'Advanced Settings',
        dataExport: 'Data Export',
        dataExportDesc: 'Export all your data including agents, call logs, and analytics.',
        requestExport: 'Request Export',
        dangerZone: 'Danger Zone',
        deleteAccount: 'Delete Account',
        deleteAccountDesc: 'Permanently delete your account and all associated data.',
        advancedConfig: 'Advanced Configuration',
        selected: 'Selected',
        installGuide: 'Installation Guide',
        stepByStep: 'Step-by-Step Instructions',
        proTip: 'Pro Implementation Tip',

        // Profile & Security
        profile: 'Profile',
        profileDesc: 'Manage your profile information and account settings',
        fullName: 'Full Name',
        enterFullName: 'Enter your full name',
        enterEmail: 'you@example.com',
        phoneLabel: 'Phone',
        enterPhone: '+1 (555) 123-4567',
        companyLabel: 'Company',
        companyPlaceholder: 'e.g., Apple',
        saving: 'Saving...',
        saveChanges: 'Save Changes',
        security: 'Security',
        passwordLabel: 'Password',
        passwordDescLabel: 'Update your password to keep your account secure',
        changePassword: 'Change Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmNewPassword: 'Confirm New Password',
        updating: 'Updating...',
        updatePassword: 'Update Password',
        profileUpdated: 'Profile updated',
        failedUpdateProfile: 'Failed to update profile',
        fillAllPassword: 'Please fill out all password fields',
        passTooShort: 'New password must be at least {min} characters',
        passMismatch: 'New password and confirmation do not match',
        passSameAsCurrent: 'New password must be different from current password',
        passUpdateSuccess: 'Password updated successfully!',
        failedUpdatePass: 'Failed to update password',

        // Common UI
        refresh: 'Refresh',
        refreshData: 'Refresh data',
        search: 'Search...',
        minutesAgo: 'minutes ago',
        hoursAgo: 'hours ago',
        yesterday: 'Yesterday',
        untitledAgent: 'Agent sans titre',
        failedToLoadUsers: 'Failed to load users.',
    },
    FR: {
        // Sidebar & Layout
        dashboard: 'Tableau de bord',
        voiceAgents: 'Agents Vocaux',
        users: 'Utilisateurs',
        analytics: 'Analytique',
        training: 'Formation',
        batchCalling: 'Appels en série',
        callLogs: "Journaux d'appels",
        billing: 'Facturation',
        settings: 'Paramètres',
        searchPlaceholder: 'Rechercher des analyses, des agents ou des journaux...',
        notifications: 'Notifications',
        logout: 'Déconnexion',
        profileSettings: 'Paramètres du profil',
        account: 'Compte',
        signOut: 'Se déconnecter',
        administrator: 'Administrateur',

        // Page Titles
        analyticsDashboard: 'Tableau de bord analytique',
        performanceAnalytics: 'Analytique de performance',
        agentTraining: 'Formation des agents',
        batchHistory: 'Historique des lots',
        createBatchCall: 'Créer un appel par lot',
        subscriptionBilling: 'Abonnement et facturation',
        accountSettings: 'Paramètres du compte',
        userProfile: 'Profil utilisateur',
        userManagement: 'Gestion des utilisateurs',
        createVoiceAgent: 'Créer un agent vocal',
        editVoiceAgent: 'Modifier l\'agent vocal',
        retrainAgent: 'Réentraîner l\'agent',
        platformOverview: 'Aperçu de la plateforme',
        aiAgents: 'Agents IA',

        // Dashboard Common
        growthVsLastMonth: 'Croissance vs mois dernier',
        totalCalls: 'Total des appels',
        successRate: 'Taux de réussite',
        avgCallDuration: 'Durée moyenne d\'appel',
        activeAgents: 'Agents actifs',
        dailyCallVolume: 'Volume d\'appels quotidien',
        callTracking30d: 'Suivi des appels des 30 derniers jours',
        monthly: 'Mensuel',
        weekly: 'Hebdomadaire',
        successRateDistribution: 'Distribution du taux de réussite',
        callOutcomesBreakdown: 'Répartition des résultats d\'appels',
        success: 'Succès',
        successful: 'Réussi',
        failed: 'Échoué',
        noAnswer: 'Pas de réponse',
        weeklyPerformance: 'Performance hebdomadaire',
        callsPerDay: 'Appels par jour',
        calls: 'Appels',
        agentPerformance: 'Performance de l\'agent',
        individualStats: 'Statistiques individuelles',
        active: 'Actif',
        busy: 'Occupé',
        fullReport: 'Rapport complet',
        newThisWeek: 'Nouveaux cette semaine',
        inactiveAgents: 'Agents inactifs',
        activeAgent: 'Agent actif',
        docsCoverage: 'Couverture des docs',
        yourAiAgents: 'Vos agents IA',
        recentActivity: 'Activité récente',
        agentCreated: 'Agent créé',
        callsSoFar: 'appels jusqu\'à présent',
        successRatePercent: 'Taux de réussite',
        createBatchCall: 'Créer un appel par lot',
        viewCallLogs: 'Voir les logs d\'appels',

        // SuperAdmin Dashboard
        welcomeBack: 'Bon retour',
        happeningPlatform: "Voici ce qui se passe sur la plateforme.",
        platformSettings: 'Paramètres de la plateforme',
        totalUsers: 'Nombre total d\'utilisateurs',
        activeSubscriptions: 'Abonnements actifs',
        newUsers7d: 'Nouveaux utilisateurs (7j)',
        recentUsers: 'Utilisateurs récents',
        viewAll: 'Voir tout',
        noUsersYet: 'Aucun utilisateur pour le moment.',
        topPerformingAgentTypes: 'Types d\'agents les plus performants',
        totalCallsCounter: 'appels au total',
        noAgentData: 'Aucune donnée de performance d\'agent.',
        topRatedAgents: 'Agents les mieux notés',
        noRatedAgents: 'Pas encore d\'agents avec assez d\'appels.',
        quickActions: 'Actions rapides',
        createNewAgent: 'Créer un nouvel agent',
        viewAnalytics: 'Voir l\'analytique',

        // Agent List & Types
        manageAgents: 'Gérez et surveillez vos agents IA',
        viewingAgentsFor: 'Affichage des agents pour',
        searchAgents: 'Rechercher des agents...',
        allTypes: 'Tous les types',
        allStatus: 'Tous les statuts',
        customer_support: 'Support client',
        lead_generation: 'Génération de prospects',
        appointment_scheduling: 'Prise de rendez-vous',
        unknown: 'Inconnu',
        support: 'Support',
        leads: 'Prospects',
        appts: 'RDVs',
        other: 'Autre',
        inactive: 'Inactif',
        avgDur: 'Durée moy',
        created: 'Créé le',
        editAgent: 'Modifier l\'agent',
        deleteAgent: 'Supprimer l\'agent',
        pause: 'Pause',
        resume: 'Reprendre',
        pausing: 'Mise en pause...',
        resuming: 'Reprise...',
        noAgentsFound: 'Aucun agent trouvé',
        noAgentsFor: 'Aucun agent pour',
        notCreatedAgents: "Vous n'avez pas encore créé d'agents IA.",
        createAgentToStart: 'créer un agent',
        toGetStarted: 'pour commencer.',
        confirmDelete: 'Êtes-vous sûr de vouloir supprimer',
        cannotBeUndone: 'Cette action est irréversible.',
        cancel: 'Annuler',
        deleting: 'Suppression...',
        editingDisabled: 'Édition désactivée pour cet agent',
        failedToLoadAgentStats: 'Échec du chargement des statistiques de l\'agent',
        errorFetchingAgents: 'Erreur lors de la récupération des agents',
        errorDeletingAgent: 'Erreur lors de la suppression de l\'agent',
        agentDeletedSuccess: 'Agent supprimé avec succès',
        agentStatusUpdated: 'Statut de l\'agent mis à jour avec succès',
        errorUpdatingStatus: 'Erreur lors de la mise à jour du statut de l\'agent',

        // Call Logs
        viewCallsAndTranscripts: 'Afficher les enregistrements et les transcriptions',
        exportCsv: 'Exporter en CSV',
        totalDuration: 'Durée totale',
        searchCalls: 'Rechercher des appels...',
        allAgents: 'Tous les agents',
        completed: 'Terminé',
        running: 'En cours',
        inProgress: 'En cours',
        missed: 'Manqué',
        noCallHistory: 'Aucun historique d\'appels',
        noCallsYetDesc: "Vous n'avez pas encore d'appels. Essayez d'ajuster les filtres ou commencez à passer des appels.",
        clearSearch: 'Effacer la recherche',
        resetFilters: 'Réinitialiser les filtres',
        callInfo: 'Infos d\'appel',
        customer: 'Client',
        agent: 'Agent',
        duration: 'Durée',
        status: 'Statut',
        summaryLabel: 'Résumé',
        inbound: 'Entrant',
        outbound: 'Sortant',
        view: 'Voir',
        showing: 'Affichage de',
        to: 'à',
        of: 'sur',
        results: 'résultats',
        previous: 'Précédent',
        next: 'Suivant',
        callSummary: 'Résumé de l\'appel',
        detailedBreakdown: 'Répartition détaillée de la conversation',
        close: 'Fermer',
        noSummaryAvailable: 'Aucun résumé disponible pour cet appel.',
        unableToLoadSummary: 'Impossible de charger le résumé pour le moment.',

        // User List
        allUsers: 'Tous les utilisateurs',
        manageUsers: 'Gérez et surveillez vos utilisateurs',
        withAgents: 'Avec agents',
        withoutAgents: 'Sans agents',
        searchUsers: 'Rechercher des utilisateurs...',
        searchUsersPlaceholder: 'Rechercher par nom, email, entreprise, rôle ou plan...',
        allVerification: 'Toutes les vérifications',
        verified: 'Vérifié',
        unverified: 'Non vérifié',
        allActivity: 'Toute l\'activité',
        company: 'Entreprise',
        minutes: 'Minutes',
        joined: 'Inscrit le',
        noUsersYetTitle: 'Aucun utilisateur',
        noUsersYetDesc: "Vous n'avez pas encore ajouté d'utilisateurs. Une fois qu'ils auront rejoint, ils apparaîtront ici.",
        viewUsersAgents: "Voir les agents de l'utilisateur",

        // Billing
        billingAndSub: 'Facturation et abonnements',
        manageSub: 'Gérez votre abonnement et vos informations de facturation',
        plansPricing: 'Plans et tarifs',
        currentSub: 'Abonnement actuel',
        billingHistory: 'Historique de facturation',
        choosePlan: 'Choisissez votre forfait',
        selectPlanDesc: 'Sélectionnez le forfait qui répond le mieux à vos besoins',
        comparePlans: 'Comparer les forfaits',
        mostPopular: 'Le plus populaire',
        activeSubscription: 'Abonnement actif',
        currentPlan: 'Forfait actuel',
        contactSales: 'Contacter le service commercial',
        freeTier: 'Niveau gratuit',
        choose: 'Choisir',
        redirecting: 'Redirection...',
        cancelSubscription: 'Annuler l\'abonnement',
        cancelSubDesc: 'Ceci mettra fin à votre abonnement payant à la fin de la période et vous fera passer au ',
        keep: 'Garder',
        confirm: 'Confirmer',
        comparePlansTitle: 'Comparer les forfaits',
        feature: 'Fonctionnalité',
        monthlyPrice: 'Prix mensuel',
        setupFee: 'Frais d\'installation',
        includedMinutes: 'Minutes incluses',
        extraMinuteRate: 'Tarif des minutes supplémentaires',
        included: 'Inclus',
        no: 'Non',
        paymentHistory: 'Historique des paiements',
        export: 'Exporter',
        redirectingToCheckout: 'Redirection vers le paiement...',
        subscriptionUpdated: 'Abonnement mis à jour.',
        checkoutCancelled: 'Paiement annulé.',
        noPaymentLink: 'Aucun lien de téléchargement disponible pour ce paiement pour le moment.',
        month: 'mois',
        perMonth: 'par mois',
        setup: 'Installation',
        monthly: 'Mensuel',
        extra: 'Supplémentaire',
        monthlyCost: 'Coût mensuel',
        autoRenewal: 'Renouvellement automatique',
        enabled: 'Activé',
        disabled: 'Désactivé',
        usageThisMonth: 'Utilisation ce mois-ci',
        minutesUsed: 'Minutes utilisées',
        remaining: 'Restant',
        overage: 'Dépassement',
        changePlan: 'Changer de forfait',
        billingHistoryTitle: 'Historique de facturation',
        payments: 'paiements',
        searchPayments: 'Rechercher des paiements...',
        noPaymentsFound: 'Aucun paiement trouvé',
        billingHistoryEmptyDesc: 'Votre historique de facturation apparaîtra ici une fois que vous aurez effectué des paiements.',
        created: 'Créé le',
        invoiceId: 'ID Facture',
        description: 'Description',
        amount: 'Montant',
        status: 'Statut',
        actions: 'Actions',
        download: 'Télécharger',
        loading: 'Chargement...',
        loadMore: 'Charger plus',
        noMorePayments: 'Plus aucun paiement',
        shown: 'affichés',
        ofSuffix: 'sur',

        // Analytics
        analytics: 'Analytique',
        analyticsDesc: 'Aperçu détaillé des performances de vos agents IA',
        totalCalls: 'Appels totaux',
        successRate: 'Taux de réussite',
        avgCallDuration: 'Durée moyenne d\'appel',
        activeAgents: 'Agents actifs',
        callsByHour: 'Appels par heure',
        callsByDay: 'Appels par jour',
        distCallsHour: 'Répartition des appels par heure',
        dailyCallVolume: 'Volume d\'appels quotidien',
        weeklyPerformance: 'Performance hebdomadaire',
        callsPerDaySuccess: 'Appels par jour avec taux de réussite',
        agentPerformance: 'Performance des agents',
        individualAgentStats: 'Statistiques individuelles des agents',
        noWeeklyData: 'Aucune donnée hebdomadaire pour cette sélection.',
        noAgentData: 'Aucune donnée de performance d\'agent disponible.',
        noBreakdownData: 'Aucune ventilation horaire ou quotidienne disponible pour cette sélection.',
        successful: 'Réussis',
        calls: 'appels',
        success: 'réussite',
        avg: 'moy',
        exportReport: 'Exporter le rapport',

        // Settings
        settings: 'Paramètres',
        settingsDesc: 'Configurez les paramètres de votre compte',
        accountSettings: 'Paramètres du compte',
        accountSettingsDesc: 'Gérez vos préférences de profil et de compte',
        profileInfo: 'Informations du profil',
        goToProfile: 'Aller au profil',
        passwordSecurity: 'Mot de passe et sécurité',
        manage: 'Gérer',
        billingSub: 'Facturation et abonnement',
        billingSubDesc: 'Gérez votre abonnement et votre facturation',
        currentPlanLabel: 'Forfait actuel',
        viewDetails: 'Voir les détails',
        advanced: 'Avancé',
        advancedDesc: 'Paramètres et configurations avancés',
        advancedSettings: 'Paramètres avancés',
        dataExport: 'Exportation de données',
        dataExportDesc: 'Exportez toutes vos données, y compris les agents, les journaux d\'appels et les analyses.',
        requestExport: 'Demander l\'exportation',
        dangerZone: 'Zone de danger',
        deleteAccount: 'Supprimer le compte',
        deleteAccountDesc: 'Supprimer définitivement votre compte et toutes les données associées.',
        advancedConfig: 'Configuration Avancée',
        selected: 'Sélectionné',
        installGuide: "Guide d'installation",
        stepByStep: 'Instructions étape par étape',
        proTip: "Conseil d'expert",

        // Profile & Security
        profile: 'Profil',
        profileDesc: 'Gérez vos informations de profil et vos paramètres de compte',
        fullName: 'Nom complet',
        enterFullName: 'Entrez votre nom complet',
        enterEmail: 'vous@exemple.com',
        phoneLabel: 'Téléphone',
        enterPhone: '+1 (555) 123-4567',
        companyLabel: 'Entreprise',
        companyPlaceholder: 'ex: Apple',
        saving: 'Enregistrement...',
        saveChanges: 'Enregistrer les modifications',
        security: 'Sécurité',
        passwordLabel: 'Mot de passe',
        passwordDescLabel: 'Mettez à jour votre mot de passe pour sécuriser votre compte',
        changePassword: 'Changer le mot de passe',
        currentPassword: 'Mot de passe actuel',
        newPassword: 'Nouveau mot de passe',
        confirmNewPassword: 'Confirmer le nouveau mot de passe',
        updating: 'Mise à jour...',
        updatePassword: 'Mettre à jour le mot de passe',
        profileUpdated: 'Profil mis à jour',
        failedUpdateProfile: 'Échec de la mise à jour du profil',
        fillAllPassword: 'Veuillez remplir tous les champs du mot de passe',
        passTooShort: 'Le nouveau mot de passe doit comporter au moins {min} caractères',
        passMismatch: 'Le nouveau mot de passe et la confirmation ne correspondent pas',
        passSameAsCurrent: 'Le nouveau mot de passe doit être différent du mot de passe actuel',
        passUpdateSuccess: 'Mot de passe mis à jour avec succès !',
        failedUpdatePass: 'Échec de la mise à jour du mot de passe',

        // Common UI
        refresh: 'Actualiser',
        refreshData: 'Actualiser les données',
        search: 'Rechercher...',
        minutesAgo: 'minutes auparavant',
        hoursAgo: 'heures auparavant',
        yesterday: 'Hier',
        untitledAgent: 'Agent sans titre',
        failedToLoadUsers: 'Échec du chargement des utilisateurs.',
    },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
    const [currentLanguage, setCurrentLanguage] = useState('EN');

    const locales = {
        EN: 'en-US',
        FR: 'fr-FR',
    };

    const t = (key) => {
        return translations[currentLanguage][key] || key;
    };

    const toggleLanguage = (lang) => {
        setCurrentLanguage(lang);
    };

    const locale = locales[currentLanguage];

    return (
        <LanguageContext.Provider value={{ currentLanguage, t, toggleLanguage, locale }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
